import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { GraduationCap, Target, Hourglass, Trash2, Zap, StickyNote, History, Info } from 'lucide-react';
import { Card } from '../ui/Card';
import { BeforeAfter, RichText, SectionTitle, VerdictBadge, useFmt, useLabels, useRuleText } from './tmShared';
import trafficManagerService, { Evolution, TrafficManager } from '../../services/trafficManagerService';

// ---------------------------------------------------------------------------
// Aprendizado: o robô medindo a si mesmo
// ---------------------------------------------------------------------------
export const LearningTab: React.FC<{ manager: TrafficManager; evolution: Evolution | null; onManagerChange: (m: TrafficManager) => void }> = ({ manager, evolution, onManagerChange }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const L = useLabels();
  const cur = manager.currency || 'BRL';
  const placar = evolution?.placar;
  const impactos = [...(evolution?.impactos || [])].reverse();
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const licoes = [...(manager.learnings || [])].reverse();
  const regrasBrutas = (manager.snapshot?.automacoes || []);
  const ruleText = useRuleText();
  const [todasRegras, setTodasRegras] = useState(false);
  // Regras iguais (mesmo nome, gatilho e ação) viram uma linha com a contagem:
  // a conta real tinha a mesma regra copiada até 8 vezes.
  const regras = (() => {
    const g: Record<string, { r: typeof regrasBrutas[number]; n: number; ligadas: number }> = {};
    for (const r of regrasBrutas) {
      const k = `${r.nome}|${r.gatilho}|${r.valor_gatilho}|${r.acao}`;
      if (!g[k]) g[k] = { r, n: 0, ligadas: 0 };
      g[k].n += 1;
      if (r.status === 'active') g[k].ligadas += 1;
    }
    return Object.values(g).sort((a, b) => b.ligadas - a.ligadas);
  })();
  const duplicadas = regras.filter((x) => x.n > 1).reduce((acc, x) => acc + x.n - 1, 0);
  const N = manager.guardrails_effective?.maturation_days ?? 7;
  const decididos = placar ? placar.melhorou + placar.piorou : 0;

  const removeLesson = async (id: string) => {
    if (!window.confirm(t('trafficManager.learning.removeConfirm', 'Apagar esta lição? O robô deixa de usá-la nas próximas rodadas.') as string)) return;
    try { onManagerChange(await trafficManagerService.removeLearning(manager.id, id)); } catch { toast.error(t('trafficManager.learning.removeError', 'Não foi possível apagar a lição')); }
  };

  const barra = (n: number, cls: string, label: string) => (placar && (placar.melhorou + placar.piorou + placar.neutro) > 0
    ? <div className={`${cls} h-full`} style={{ width: `${(n / (placar.melhorou + placar.piorou + placar.neutro)) * 100}%` }} title={`${label}: ${n}`} />
    : null);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <Card>
          <SectionTitle icon={Target}>{t('trafficManager.learning.scoreTitle', 'Placar do robô')}</SectionTitle>
          <div className="text-4xl font-bold text-gray-900 dark:text-white tabular-nums">{placar && placar.taxa_acerto != null ? `${placar.taxa_acerto}%` : '—'}</div>
          <p className="text-sm text-gray-500">{decididos ? t('trafficManager.learning.scoreSub', 'das {{n}} mudanças com resultado claro melhoraram a conta', { n: decididos }) : t('trafficManager.learning.scoreEmpty', 'Ainda sem mudanças maturadas com resultado claro.')}</p>
          {placar && (
            <>
              <div className="mt-4 flex h-2.5 rounded-full overflow-hidden gap-0.5 bg-gray-100 dark:bg-gray-700">
                {barra(placar.melhorou, 'bg-emerald-500', t('trafficManager.verdict.melhorou', 'Melhorou'))}
                {barra(placar.neutro, 'bg-gray-400', t('trafficManager.verdict.neutro', 'Sem efeito claro'))}
                {barra(placar.piorou, 'bg-red-500', t('trafficManager.verdict.piorou', 'Piorou'))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <VerdictBadge v="melhorou" /><span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">{placar.melhorou}</span>
                <VerdictBadge v="neutro" /><span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">{placar.neutro}</span>
                <VerdictBadge v="piorou" /><span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">{placar.piorou}</span>
                <VerdictBadge v="maturando" /><span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">{placar.maturando}</span>
                <VerdictBadge v="inconclusivo" /><span className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">{placar.inconclusivo}</span>
              </div>
            </>
          )}
        </Card>
        <Card className="lg:col-span-2">
          <SectionTitle icon={Hourglass}>{t('trafficManager.learning.howTitle', 'Como ele aprende')}</SectionTitle>
          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-disc pl-5">
            <li>{t('trafficManager.learning.how1', 'Cada mudança fica {{n}} dias completos maturando. Só depois o robô compara {{n}} dias antes com {{n}} dias depois, pelo objetivo da conta.', { n: N })}</li>
            <li>{t('trafficManager.learning.how2', 'Se outra mudança caiu na mesma campanha no período, ou o volume é pequeno, o resultado é inconclusivo: ele não aprende com ruído.')}</li>
            <li>{t('trafficManager.learning.how3', 'Em toda rodada ele vê esse placar e o próprio playbook, repete o que funcionou nesta conta e evita o que piorou.')}</li>
            <li>{t('trafficManager.learning.how4', 'Enquanto uma campanha matura, ele não mexe de novo no orçamento dela, e nunca desfaz o que fez dentro da janela anti-vaivém.')}</li>
          </ul>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <Card>
          <SectionTitle icon={GraduationCap}>{t('trafficManager.learning.playbook', 'Playbook aprendido nesta conta')}</SectionTitle>
          {licoes.length === 0 && <p className="text-sm text-gray-500">{t('trafficManager.learning.playbookEmpty', 'Nenhuma lição ainda. Elas aparecem quando as mudanças maturam e mostram um resultado claro.')}</p>}
          <ul className="space-y-2">
            {licoes.map((l) => (
              <li key={l.id} className="flex gap-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                <GraduationCap className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-gray-800 dark:text-gray-200">{l.text}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{f.dateTime(l.at)}</p>
                </div>
                <button type="button" onClick={() => removeLesson(l.id)} className="text-gray-400 hover:text-red-500 shrink-0" aria-label={t('trafficManager.learning.remove', 'Apagar lição') as string}><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
          {manager.memory && (
            <div className="mt-5">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5"><StickyNote className="w-4 h-4" />{t('trafficManager.learning.watching', 'O que ele está observando agora')}</h3>
              <RichText text={manager.memory} className="text-sm text-gray-600 dark:text-gray-300" />
            </div>
          )}
        </Card>

        <Card>
          <SectionTitle icon={Zap}>{t('trafficManager.learning.automations', 'Automações destas campanhas')}</SectionTitle>
          <p className="text-sm text-gray-500 -mt-2 mb-3">{t('trafficManager.learning.automationsHelp', 'Regras que a plataforma avalia a cada 30 minutos, entre as rodadas. O robô cria regras de proteção e desliga as que brigam com o plano.')}</p>
          {regras.length === 0 && <p className="text-sm text-gray-500">{t('trafficManager.learning.noRules', 'Nenhuma regra de automação nestas campanhas.')}</p>}
          {duplicadas > 0 && (
            <p className="mb-2 text-xs text-amber-800 dark:text-amber-300 flex gap-1.5"><Info className="w-4 h-4 shrink-0" />{t('trafficManager.learning.duplicates', '{{n}} regra(s) repetida(s) nestas campanhas. Regras iguais disparam a mesma ação mais de uma vez; vale limpar em Automações.', { n: duplicadas })}</p>
          )}
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {(todasRegras ? regras : regras.slice(0, 8)).map(({ r, n, ligadas }) => (
              <li key={r.id} className="py-2.5 text-sm flex items-start gap-2">
                <span className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${ligadas ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-gray-900 dark:text-white truncate">{r.nome}{n > 1 && <span className="ml-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">×{n}</span>}</div>
                  <div className="text-xs text-gray-500">{ruleText(r.gatilho, r.valor_gatilho, r.acao, cur)} · {ligadas ? (n > 1 ? t('trafficManager.learning.onCount', '{{a}} de {{n}} ligadas', { a: ligadas, n }) : t('trafficManager.learning.on', 'ligada')) : t('trafficManager.learning.off', 'desligada')}{r.criada_pelo_robo ? ` · ${t('trafficManager.learning.byRobot', 'criada pelo robô')}` : ''}</div>
                </div>
              </li>
            ))}
          </ul>
          {regras.length > 8 && (
            <button type="button" onClick={() => setTodasRegras((v) => !v)} className="mt-2 text-sm text-primary-600 hover:underline">
              {todasRegras ? t('trafficManager.overview.showLess', 'Mostrar menos') : t('trafficManager.overview.showAll', 'Ver todas ({{n}})', { n: regras.length })}
            </button>
          )}
        </Card>
      </div>

      <Card>
        <SectionTitle icon={History}>{t('trafficManager.learning.effects', 'Efeito de cada mudança')}</SectionTitle>
        {impactos.length === 0 && <p className="text-sm text-gray-500">{t('trafficManager.learning.effectsEmpty', 'Nenhuma mudança avaliável ainda.')}</p>}
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {impactos.map((i, idx) => (
            <div key={idx} className="py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{L.action(i.tool)}</span>
                <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[18rem]">· {i.campaign_name}</span>
                {i.tool === 'set_daily_budget' && i.plan && <span className="text-sm text-gray-700 dark:text-gray-200">{f.money(i.plan.de, cur)} → {f.money(i.plan.para, cur)}</span>}
                <VerdictBadge v={i.veredito} daysLeft={i.dias_faltando} reason={i.motivo_veredito} />
                <span className="text-xs text-gray-400">{f.day(i.dia)}</span>
                {i.veredito !== 'maturando' && (
                  <button type="button" className="text-xs text-primary-600 hover:underline" onClick={() => setOpen((p) => ({ ...p, [idx]: !p[idx] }))}>
                    {open[idx] ? t('trafficManager.changes.hideEffect', 'esconder efeito') : t('trafficManager.changes.showEffect', 'ver efeito')}
                  </button>
                )}
              </div>
              {i.motivo_veredito && <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1"><Info className="w-3 h-3" />{i.veredito === 'inconclusivo' && i.motivo_veredito.startsWith('outra') ? t('trafficManager.learning.mixed', 'Outra mudança na mesma campanha no período: não dá para atribuir o efeito.') : t('trafficManager.learning.lowVolume', 'Pouco volume para separar efeito de ruído.')}</p>}
              {open[idx] && <div className="mt-2"><BeforeAfter antes={i.antes} depois={i.depois} cur={cur} /></div>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

